import {hasValue} from '@famly/stat_ts-utils_has-value';
import {getFromLocalStorage} from '@famly/stat_ts-utils_local-storage';

export type Crew = 'enr' | 'stat' | 'fin' | 'inv' | 'pay' | 'gro' | 'ptp' | 'fnd' | 'tail' | 'go';

export type FeatureFlag = {
    flag: string;
    title: string;
    description?: string;
    crew: Crew;
};

export type ToolingFlag = {
    flag: string;
    title: string;
    description?: string;
};

export type DebuggerFlags = {
    [flagId: string]: FeatureFlag | ToolingFlag;
};

export const DebugFeatureLocalStorageFlags = {
    CARE_CONTRACTS: {
        flag: 'CARE_CONTRACTS',
        title: 'Enable Care Contracts',
        description: 'Toggle this on to enable the care contracts feature.',
        crew: 'enr',
    },
    NEW_STARTERS_ONBOARDING: {
        flag: 'NEW_STARTERS_ONBOARDING',
        title: 'Enable New Starters Onboarding',
        description: 'Toggle this on to enable the new starters onboarding feature.',
        crew: 'enr',
    },
    SHOW_DEFAULT_FTE: {
        flag: 'SHOW_DEFAULT_FTE',
        title: 'Show default FTE',
        description: 'Toggle this to show default fte.',
        crew: 'stat',
    },
    UNIQUE_PARENT_EXPERIENCE: {
        flag: 'UNIQUE_PARENT_EXPERIENCE',
        title: 'Unique parent experience home and explore',
        description: 'Toggle this to show unique parent experience home and explore.',
        crew: 'enr',
    },
    SHOW_FINANCE_DEBUGGING_BUTTON: {
        flag: 'SHOW_FINANCE_DEBUGGING_BUTTON',
        title: 'Show finance debugging button',
        description: 'Show finance debugging button to access Finance debugging page.',
        crew: 'fin',
    },
    SHOW_BULK_PLAN_DELETE: {
        flag: 'SHOW_BULK_PLAN_DELETE',
        title: 'Show Bulk Plan delete option',
        description: 'Toggle this to show the bulk delete latest plan in plans page',
        crew: 'inv',
    },
    SHOW_BULK_BILLING_PROFILE_UPDATE: {
        flag: 'SHOW_BULK_BILLING_PROFILE_UPDATE',
        title: 'Show Bulk Billing Profile Update in the Plans page',
        description: 'Used to handle bulk billing profile update in the Plans page',
        crew: 'inv',
    },
    ENABLE_MIGRATE_V1_TO_V2: {
        flag: 'ENABLE_MIGRATE_V1_TO_V2',
        title: 'Enable V1 to V2 billing profile migration',
        description:
            'Toggle this on to enable the bulk migration flow from V1 billing profiles (ACTUAL_SESSIONS, ANNUALIZED, ANNUALIZED_MID_MONTH) to their V2 equivalents (ACTUALS_V2, ANNUALIZED_V2).',
        crew: 'inv',
    },
    FAMLY_HOME_CATEGORY_ONBOARDING: {
        flag: 'FAMLY_HOME_CATEGORY_ONBOARDING',
        title: 'Show Category Onboarding widget in Famly Home',
        description: 'Used to display the work in progress widget that will be in Famly Home',
        crew: 'gro',
    },
    NEW_FORM_SUBMIT_PAGE_LAYOUT: {
        flag: 'NEW_FORM_SUBMIT_PAGE_LAYOUT',
        title: 'New form submit page layout',
        description: 'Turn this on to enable the new form submit page layout.',
        crew: 'ptp',
    },
    MOBILE_APP_PACKAGE_PURCHASE_MAGIC_LINK_REDIRECT: {
        flag: 'MOBILE_APP_PACKAGE_PURCHASE_MAGIC_LINK_REDIRECT',
        title: 'Mobile app package purchase magic link redirect',
        crew: 'gro',
    },
    MEAL_PLANS_DND: {
        flag: 'MEAL_PLANS_DND',
        title: 'Enable dnd of food items in meal plans',
        crew: 'ptp',
    },
    BULK_ADD_ATTENDANCE: {
        flag: 'BULK_ADD_ATTENDANCE',
        title: 'Show the bulk add attendance modal',
        crew: 'stat',
    },
    SMS_NOTIFICATIONS: {
        flag: 'SMS_NOTIFICATIONS',
        title: 'Enable SMS notifications in Newsfeed and Messages',
        crew: 'fnd',
    },
    SHOW_PURCHASES_NEW_DESIGN_ON_MOBILE: {
        flag: 'SHOW_PURCHASES_NEW_DESIGN_ON_MOBILE',
        title: 'Show new design for purchases on mobile and automatic pricing',
        crew: 'gro',
    },
    MF_LISTS_MANAGE_CHILDREN: {
        flag: 'MF_LISTS_MANAGE_CHILDREN',
        title: 'Enable manage children MF List implementation',
        crew: 'fnd',
    },
    NEW_REPORT_CREATE_FLOW: {
        flag: 'NEW_REPORT_CREATE_FLOW',
        title: 'Enable new "Create report" flow',
        description: 'Enables the new "Create report" flow on reports pages.',
        crew: 'fnd',
    },
    LIMITED_CONTACT_ROLE: {
        flag: 'LIMITED_CONTACT_ROLE',
        title: 'Enable Limited Contact Role toggle',
        description: 'Shows the Limited Contact Role toggle in the child profile contacts form.',
        crew: 'fnd',
    },
    BILL_PAYER_ENABLE_LEGACY_CSV_REPORT: {
        flag: 'BILL_PAYER_ENABLE_LEGACY_CSV_REPORT',
        title: 'Enables the legacy PHP-based CSV report for bill payers',
        description:
            'While testing the new BillPayer report, it might be useful to have the old one available as well for testing. This enables the legacy report.',
        crew: 'pay',
    },
    FAMLY_HOME_NEW_LAYOUT: {
        flag: 'FAMLY_HOME_NEW_LAYOUT',
        title: 'Enable new Famly Home layout (prototype)',
        description: 'The new layout will remove the sidebar and move all widgets into the primary widget area',
        crew: 'fnd',
    },
    NEW_PROFILE_MENU_LOCATION: {
        flag: 'NEW_PROFILE_MENU_LOCATION',
        title: 'Enable new profile menu location',
        description:
            'Toggle this on to use the new location in the sidebar for the profile menu. Also hides the profile menu in the topbar.',
        crew: 'fnd',
    },
    INTENDED_AUDIENCE_FOR_ACTIVITIES_AND_RESOURCES: {
        flag: 'INTENDED_AUDIENCE_FOR_ACTIVITIES_AND_RESOURCES',
        title: 'Enable intended audience for activities and resources',
        description: 'Toggle this on to enable intended audience for activities and resources.',
        crew: 'ptp',
    },
    DISABLE_CHILD_PROFILE_INSERT_PLAN_DIVIDERS: {
        flag: 'DISABLE_CHILD_PROFILE_INSERT_PLAN_DIVIDERS',
        title: 'Disable insert plan dividers in Child Profile',
        description:
            'Disables the dividers in the plan overview in the Child Profile for inserting plans between existing plans.',
        crew: 'ptp',
    },
    NEW_FUNDING_LOOP: {
        flag: 'NEW_FUNDING_LOOP',
        title: 'Enable new funding loop tabs',
        description: 'Toggle this on to enable the new funding loop tabs in the public funding report.',
        crew: 'inv',
    },
    REDESIGN_CHART_OF_ACCOUNTS: {
        flag: 'REDESIGN_CHART_OF_ACCOUNTS',
        title: 'Enable redesign of chart of accounts',
        description: 'Toggle this on to enable the redesign of chart of accounts.',
        crew: 'inv',
    },
    MANAGE_EXTERNAL_IDS: {
        flag: 'MANAGE_EXTERNAL_IDS',
        title: 'Show Manage External IDs component',
        description: 'Toggle this on to show the Manage External IDs component in integration settings.',
        crew: 'fnd',
    },
    CARE_REQUIREMENTS: {
        flag: 'CARE_REQUIREMENTS',
        title: 'Enable care requirements',
        description: 'Toggle this on to enable care requirements.',
        crew: 'stat',
    },
    NEW_AGE_GROUPS: {
        flag: 'NEW_AGE_GROUPS',
        title: 'Enable new age groups',
        description: 'Toggle this on to enable new age groups.',
        crew: 'stat',
    },
    CARE_REQUIREMENTS_M2: {
        flag: 'CARE_REQUIREMENTS_M2',
        title: 'Enable care requirements (milestone 2)',
        description: 'Toggle this on to enable care requirements milestone 2.',
        crew: 'stat',
    },
    CARE_REQUIREMENTS_M3: {
        flag: 'CARE_REQUIREMENTS_M3',
        title: 'Enable care requirements (milestone 3)',
        description: 'Toggle this on to enable care requirements milestone 3.',
        crew: 'stat',
    },
    CARE_REQUIREMENTS_OPT_IN: {
        flag: 'CARE_REQUIREMENTS_OPT_IN',
        title: 'Enable care requirements opt-in toggle',
        description:
            'Toggle this on to enable care requirements opt-in. This will make sure that the feature toggle opt in is shown on the Playground page',
        crew: 'stat',
    },
    AGENT_CHAT: {
        flag: 'AGENT_CHAT',
        title: 'Enable Agent Chat Global button',
        description: 'Shows the Agent Chat Global button.',
        crew: 'gro',
    },
    DEVICE_ENROLLMENT: {
        flag: 'DEVICE_ENROLLMENT',
        title: 'Device Enrollment',
        description: 'Toggle this on to enable device enrollment features.',
        crew: 'fnd',
    },
    USE_US_NAVIGATION: {
        flag: 'USE_US_NAVIGATION',
        title: 'Use the new US navigation',
        description: 'Use the new US navigation',
        crew: 'gro',
    },
    TABS_IN_TOPBAR: {
        flag: 'TABS_IN_TOPBAR',
        title: 'Enable tabs in topbar',
        description: 'Toggle this on to show tabs in topbar.',
        crew: 'gro',
    },
    NEWSFEED_POST_PINNING: {
        flag: 'NEWSFEED_POST_PINNING',
        title: 'Enable Newsfeed Post Pinning',
        description:
            'Enabling this will let users pin newsfeed posts and see the pinned post. This works only for posts that are directed (recipients) to the entire site.',
        crew: 'go',
    },
    AUTOPAY_MODAL_REDESIGN: {
        flag: 'AUTOPAY_MODAL_REDESIGN',
        title: 'Enable new autopay modal redesign',
        description:
            'Uses the new autopay modal with redesigned UI for setting up and managing automatic payments.',
        crew: 'pay',
    },
} as const satisfies Record<string, FeatureFlag>;

export const ToolingLocalStorageFlags = {
    STICKY_DEBUGGING: {
        flag: 'STICKY_DEBUGGING',
        title: 'Always show the "Debugging" menu',
        description: 'Toggle this on to always show the debugging menu.',
    },
    ENABLE_INTERCOM_TEST_ENVIRONMENT: {
        flag: 'ENABLE_INTERCOM_TEST_ENVIRONMENT',
        title: 'Connect to Intercom test environment',
        description: 'Connect to Intercom test environment for debugging/dev purposes.',
    },
    SHOW_AUTO_LOGOUT_STATS: {
        flag: 'SHOW_AUTO_LOGOUT_STATS',
        title: 'Auto logout V2 status',
        description:
            'Toggle this to get an overlay that shows all details for auto logout. Auto logout feature and V2 needs to be enabled.',
    },
    TRACKING_VISUALIZATION: {
        flag: 'TRACKING_VISUALIZATION',
        title: 'Enable tracking visualisation of Intercom and Mixpanel events',
        description:
            'Enables the unified tracking visualisation tool for both Mixpanel and Intercom events. You can see tracking events for elements, control which events are shown/sent, and view a searchable live feed of events. Make sure the feature "ENABLE_ANALYTICS" is enabled as well.',
    },
    FORM_BUILDER_DEBUG_INFO: {
        flag: 'FORM_BUILDER_DEBUG_INFO',
        title: 'Adds debug info to the form builder state (for e2e)',
        description: 'Toggle this to add debug info to the form builder state (for e2e)',
    },
    TRACE_ALL_TRANSACTIONS: {
        flag: 'TRACE_ALL_TRANSACTIONS',
        title: '[Sentry] Trace all transactions',
        description: 'Turn this on to have all transactions traced in Sentry.',
    },
    SENTRY_ENABLE_REPLAY: {
        flag: 'ENABLE_REPLAY',
        title: '[Sentry] Enable Replay',
        description: 'Turn this on to enable Sentry replay.',
    },
    SENTRY_DIAGNOSTIC_MODE: {
        flag: 'SENTRY_DIAGNOSTIC_MODE',
        title: '[Sentry] Diagnostic mode',
        description:
            "Turn this on to enable Sentry diagnostic mode. This will bypass the filtering we've setup in sentry-setup.ts.",
    },
    SHOW_STAT_FORM_DEBUG: {
        flag: 'SHOW_STAT_FORM_DEBUG',
        title: 'Enable stat form debug information',
    },
    ENABLE_FORM_BUILDER_DEBUG: {
        flag: 'ENABLE_FORM_BUILDER_DEBUG',
        title: 'Enable form builder debug info',
        description: 'Toggle this on to access the form builder debug info',
    },
    ENABLE_IMAGICK_IMAGE_PROCESSING: {
        flag: 'ENABLE_IMAGICK_IMAGE_PROCESSING',
        title: 'Enable Imagick image processing',
        description: 'Toggle this on to use Imagick for image processing instead of GD (in core).',
    },
} as const satisfies Record<string, ToolingFlag>;

export const DebugLocalStorageFlags = {
    ...DebugFeatureLocalStorageFlags,
    ...ToolingLocalStorageFlags,
};

/**
 * I grew tired of having to import DebugLocalStorageFlags to check if a flag is enabled
 * so I added an overload that allows you to simply look up by the union of keys in DebugLocalStorageFlags
 */
interface CheckIfFlagIsToggled {
    (flag: string): boolean;
    (flag: keyof typeof DebugLocalStorageFlags): boolean;
}
export const checkIfFlagIsToggled: CheckIfFlagIsToggled = flag => {
    const lookup = DebugLocalStorageFlags[flag as keyof typeof DebugLocalStorageFlags];
    if (hasValue(lookup)) {
        return Boolean(getFromLocalStorage(lookup.flag));
    }
    return Boolean(getFromLocalStorage(flag));
};
